import { useCallback } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { showWarning } from "src/utils/toast/toastUtil";
import { validateItemAccount, isDuplicateChildItemcd, restoreOriginalValues } from "../Util_MST1020_R01";
import { MST1020_R01_BomDto } from "../Dto_MST1020_R01";
import { ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";

export function useBomCellChange({
	MST1020_R01BomList,
	updateMST1020_R01Bom,
	originalMST1020_R01BomList,
	settingCodeSearchModalProps,
	gridRef
}: any) {
	// 자품목코드 자동 검색 함수
	const handleChildItemCodeSearch = useCallback(
		async (itemCode: string, rowData: MST1020_R01_BomDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim()) {
				updateMST1020_R01Bom(rowData.valueIndex!, {
					...rowData,
					childItemcd: '',
					childItemnm: '',
					itemSpec: '',
					locationCd: '',
					locationNm: '',
					childItemUnit: '',
					isModified: rowData.inserted ? false : true,
				});
				return;
			}
			const originalData = {
				...rowData,
				childItemcd:		oldValue || rowData.childItemcd,
				childItemnm:		rowData.childItemnm,
				itemSpec:			rowData.itemSpec,
				locationCd:			rowData.locationCd ?? '',
				locationNm:			rowData.locationNm ?? '',
				childItemUnit:		rowData.childItemUnit
			};
			try {
				const response			= await getItemPopupList(itemCode, ALLOWED_ITEM_ACCOUNTS, '', '');
				const foundItemByCode	= response.data.data.find((item: any) => item.itemCd === itemCode);
				if (foundItemByCode) {
					if (foundItemByCode.itemAccount && !validateItemAccount(foundItemByCode.itemAccount)) {
						showWarning(
							`선택한 품목(${foundItemByCode.itemNm})은 BOM 등록이 불가능한 계정입니다.\n허용 품목 계정: 재공품, 원자재, 부자재`,
							5000
						);
						restoreOriginalValues(rowData.valueIndex!, originalData, updateMST1020_R01Bom, gridRef);
						settingCodeSearchModalProps(
							'품목',
							itemCode,
							false,
							'childItemcd',
							rowData,
							rowData.valueIndex!,
							{ itemAccount: ALLOWED_ITEM_ACCOUNTS },
						);
						return;
					}
					const duplicateCount = isDuplicateChildItemcd(MST1020_R01BomList, foundItemByCode.itemCd, rowData.valueIndex);
					if (duplicateCount) {
						showWarning(`경고: 자품목코드 '${foundItemByCode.itemNm}'가 이미 존재합니다. (2개)`);
						restoreOriginalValues(rowData.valueIndex!, originalData, updateMST1020_R01Bom, gridRef);
						return;
					}
					updateMST1020_R01Bom(rowData.valueIndex!, {
						...rowData,
						childItemcd:		foundItemByCode.itemCd		|| '',
						childItemnm:			foundItemByCode.itemNm		|| '',
						itemSpec:				foundItemByCode.itemSpec		|| '',
						locationCd:				foundItemByCode.locationCd	|| '',
						locationNm:				foundItemByCode.locationNm	|| '',
						childItemUnit:			foundItemByCode.itemUnit		|| '',
						bomUpdateNo:			rowData.bomUpdateNo			|| '1',
						isModified:				true
					});
				} else {
					settingCodeSearchModalProps(
						'품목',
						itemCode,
						false,
						'childItemcd',
						rowData,
						rowData.valueIndex!,
						{ itemAccount: ALLOWED_ITEM_ACCOUNTS }
					);
				}
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateMST1020_R01Bom, gridRef);
				settingCodeSearchModalProps(
					'품목',
					itemCode || 'all',
					false,
					'childItemcd',
					rowData,
					rowData.valueIndex!,
					{ itemAccount: ALLOWED_ITEM_ACCOUNTS }
				);
			}
		},
		[
			MST1020_R01BomList,
			updateMST1020_R01Bom,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	// 커스텀 셀 변경 핸들러
	const handleCellChange = useCallback(
		(event: any) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field)	return;
			const field = colDef.field as keyof MST1020_R01_BomDto;
			if (field === 'childItemcd' && newValue !== oldValue) {
				handleChildItemCodeSearch(newValue, data, oldValue);
				return;
			}
			
			const originalData =
				originalMST1020_R01BomList.find(
					(item: MST1020_R01_BomDto) => item.valueIndex === data.valueIndex
				);
			const isModified =
				data.inserted === true
					? false
					: (originalData ? originalData[field] !== newValue : true);
			
			let updatedData = {
				...data,
				[field]: newValue,
				bomUpdateNo: data.bomUpdateNo || '1',
				isModified: true
			};
			
			updateMST1020_R01Bom(data.valueIndex!, updatedData);
		},
		[
			handleChildItemCodeSearch,
			originalMST1020_R01BomList,
			updateMST1020_R01Bom
		]
	);

	return {
		handleCellChange,
		handleChildItemCodeSearch
	};
}